/**
 * The one place that talks to PUT /api/projects/{id}/screenplay.
 * Extracted from the component so the wire contract is unit-testable:
 * payload shape, keepalive byte math, revision handling, error statuses.
 */
import { authFetch } from '../lib/authFetch';

// Browsers cap the TOTAL in-flight keepalive body quota at ~64KB — and the
// limit is BYTES, not UTF-16 code units. A Swahili/Amharic script measured
// with .length would blow the quota and Chrome rejects the fetch outright.
export const KEEPALIVE_BODY_LIMIT_BYTES = 60_000;

export function bodyByteSize(body) {
  return new Blob([body]).size;
}

/**
 * Pick the base rev a freshly-mounted save client should start from, in
 * strict priority (review F4). Exported so the exact rule is unit-tested,
 * not re-implemented in a test:
 *   1. docRef.rev  — the rev this session's own autosaves advanced to,
 *      carried across a tab round-trip in the mount-surviving ref channel.
 *   2. loaded.rev  — the detail-endpoint rev on a genuine first mount.
 *   3. listRev     — the /projects LIST rev; stale the instant any save
 *      rotates it, so it is the last resort only.
 * `undefined`/`null` at a level falls through to the next (a real rev is
 * always a non-empty hex string; null is the legitimate "never saved" seed
 * and only surfaces from level 2/3, never masks a survived rev).
 */
export function seedScreenplayRev(docRef, loaded, listRev) {
  if (docRef?.rev != null) return docRef.rev;
  if (loaded?.rev !== undefined) return loaded.rev;
  return listRev ?? null;
}

/**
 * Returns { save } for one project.
 *  - save(doc, meta): PUTs the document. meta.unload marks a page-hide/close
 *    flush — the only context where keepalive is used (and only when the
 *    body fits the byte quota; a normal save never needs keepalive).
 *  - Identical consecutive documents are skipped without a network call
 *    (autosave fires on a debounce, not a diff — don't re-upload sameness).
 *  - Optimistic concurrency: sends the last-seen rev; the server 409s a
 *    stale write (another tab, or a V2b restore) instead of letting it
 *    silently overwrite newer content. Errors carry .status so the
 *    autosaver can tell permanent rejects from transient failures.
 *  - onRev(newRev): called after every successful save with the server's
 *    rotated rev. The caller pushes it into a mount-surviving channel
 *    (docRef.rev) so a tab round-trip re-seeds this client from the rev its
 *    OWN autosaves advanced to — not the stale page-load list rev, which
 *    would 409 the writer's next save with no real conflict (review F4).
 */
export function createScreenplaySave(apiBase, projectId, initialRev = null, onRev = null) {
  let rev = initialRev ?? null;
  let lastSavedDocJson = null;

  const save = async (doc, meta = {}) => {
    const docJson = JSON.stringify(doc);
    if (docJson === lastSavedDocJson) return; // nothing changed — no traffic
    const body = JSON.stringify({ document: doc, base_rev: rev });
    const res = await authFetch(`${apiBase}/projects/${projectId}/screenplay`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: Boolean(meta.unload) && bodyByteSize(body) < KEEPALIVE_BODY_LIMIT_BYTES,
    });
    if (!res.ok) {
      const detail = await res.json().catch(() => ({}));
      const err = new Error(detail.detail || `Save failed (${res.status})`);
      err.status = res.status;
      throw err;
    }
    const data = await res.json().catch(() => ({}));
    if (data.rev) {
      rev = data.rev;
      onRev?.(rev);
    }
    lastSavedDocJson = docJson;
  };

  return { save };
}
